import { PanelOrientationEnum } from "@duet/shared/pvComponents/projectParameters";
import {
  Feature,
  FeatureCollection,
  Polygon,
  bearing,
  booleanContains,
  buffer,
  center,
  destination,
  distance,
  envelope,
  featureCollection,
  lineString,
  nearestPoint,
} from "@turf/turf";
import { BlockLayer, PerimeterLayer } from "./BlockLayerModel";
import { UNASSIGNED_BLOCK_ID } from "./BlockModel";

export interface FrameCollectedMetadata {
  groupId: string;
  name: string;
  num_strings: number;
  num_cols: number;
  num_tiers: number;
  orientation_str: string;
}

export interface FramePopulation {
  id: string;
  name: string;
}

export interface FrameDimensions {
  group: string;
  short: number;
  long: number;
}

export interface FrameModuleDimensions {
  module_width: number;
  module_height: number;
  module_x_spacing: number;
  module_y_spacing: number;
}

export interface FrameTracking {
  tracker_type: string;
  min_phi: number;
  max_phi: number;
  min_theta?: number;
  max_theta?: number;
}

export interface FrameTableMetadata {
  num_strings: number;
  num_cols: number;
  num_tiers: number;
  orientation_str: PanelOrientationEnum;
}

export interface FrameProperties {
  population?: FramePopulation;
  azimuth?: number;
  fixedTilt?: number;
  frameDimensions: FrameDimensions;
  moduleDimensions?: FrameModuleDimensions;
  tracking?: FrameTracking;
  tableMetadata: FrameTableMetadata;
}

export type FrameFeature<P = FrameProperties> = Feature<Polygon, P>;

export function getBearing(polygon: Polygon) {
  const [[p0, p1, p2]] = polygon.coordinates;
  const [first, second] = [distance(p0, p1), distance(p1, p2)];

  if (first < second) {
    return bearing(p0, p1);
  }

  return bearing(p1, p2);
}

/**
 * Get a LineString segment to span a collection of features and test for intersections
 * @param collection
 * @param bearing
 * @returns
 */
export function makeIntersectionLine(
  collection: FeatureCollection,
  bearing: number
) {
  const bbox = envelope(collection);
  const centers = collection.features.map((feature) => center(feature));
  const origin = nearestPoint(center(bbox), featureCollection(centers));

  // In the worst case the frame nearest to the bounding box center may
  // still be somewhat far away so we'll cast maximum distance of the
  // bounding box.
  const castDistance = distance(
    bbox.geometry.coordinates[0][0],
    bbox.geometry.coordinates[0][1],
    { units: "meters" }
  );

  const start = destination(
    origin.geometry.coordinates,
    castDistance,
    bearing,
    { units: "meters" }
  );

  const end = destination(
    origin.geometry.coordinates,
    castDistance,
    bearing + 180,
    { units: "meters" }
  );

  return lineString([start.geometry.coordinates, end.geometry.coordinates]);
}

export function partitionedFramesSelector(
  blockPerimeterLayers: BlockLayer[],
  framesLayers: BlockLayer[]
) {
  // TODO: Move this logic into @duet/shared
  const frames = framesLayers.flatMap(
    (layer) => (layer.geojson as FeatureCollection).features
  ) as FrameFeature[];

  if (!frames.length) {
    return {};
  }

  const expandedPerimeters = (blockPerimeterLayers as PerimeterLayer[]).map(
    (layer) => buffer(layer.geojson, 0.5, { units: "meters" })
  );

  return frames.reduce<Record<string, typeof frames>>(
    (acc, frame: FrameFeature) => {
      const index = expandedPerimeters.findIndex((polygon) =>
        booleanContains(polygon, frame)
      );

      const perimeter = blockPerimeterLayers[index];
      const blockId = perimeter?.block_id || UNASSIGNED_BLOCK_ID;
      if (!acc[blockId]) {
        acc[blockId] = [];
      }

      acc[blockId].push(frame);

      return acc;
    },
    {
      [UNASSIGNED_BLOCK_ID]: [],
    }
  );
}
